import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const KEY = "spectraloom_inapp_banner_dismissed";

/**
 * Detect whether the page is being rendered inside a mobile app's embedded
 * webview (Instagram / Facebook / TikTok / LinkedIn / X / Threads / etc.).
 * These webviews trigger Google's `disallowed_useragent` policy on OAuth,
 * which breaks the "Sign in with Google" flow · so we invite users to open
 * the site in Safari/Chrome instead.
 *
 * Returns the detected app name (nice label) or null when we're in a
 * normal browser.
 */
function detectInAppBrowser() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";

  // Order matters · some apps embed multiple markers (e.g. FBAN + Instagram
  // when opening from FB → IG cross-app links).
  const rules = [
    { pattern: /Instagram/i, name: "Instagram" },
    { pattern: /FBAN|FBAV|FB_IAB|FBIOS/i, name: "Facebook" },
    { pattern: /TikTok|musical_ly|BytedanceWebview/i, name: "TikTok" },
    { pattern: /LinkedInApp/i, name: "LinkedIn" },
    { pattern: /Twitter/i, name: "X" },
    { pattern: /Threads/i, name: "Threads" },
    { pattern: /Snapchat/i, name: "Snapchat" },
    { pattern: /Line\//i, name: "LINE" },
    { pattern: /MicroMessenger/i, name: "WeChat" },
    { pattern: /Pinterest/i, name: "Pinterest" },
  ];
  for (const r of rules) {
    if (r.pattern.test(ua)) return r.name;
  }
  return null;
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function InAppBrowserBanner() {
  const { t } = useTranslation();
  const app = useMemo(() => detectInAppBrowser(), []);
  const platform = useMemo(() => detectPlatform(), []);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!app) return;
    try {
      if (!sessionStorage.getItem(KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, [app]);

  if (!app || !visible) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(KEY, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const browserLabel = platform === "ios" ? "Safari" : platform === "android" ? "Chrome" : "Safari/Chrome";

  const stepsIos = [
    t("inapp_banner.step_ios_1"),
    t("inapp_banner.step_ios_2"),
  ];
  const stepsAndroid = [
    t("inapp_banner.step_android_1"),
    t("inapp_banner.step_android_2"),
  ];
  const steps = platform === "android" ? stepsAndroid : stepsIos;

  return (
    <div
      className="sticky top-0 left-0 right-0 z-[95] bg-amber-100 text-amber-950 border-b border-amber-300 shadow-sm"
      data-testid="inapp-browser-banner"
      role="region"
      aria-label={t("inapp_banner.aria")}
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />
        <div className="flex-1 text-sm leading-snug">
          <div>
            <span className="font-semibold">{t("inapp_banner.title", { app })}</span>{" "}
            {t("inapp_banner.body", { browser: browserLabel })}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-amber-900 font-medium underline underline-offset-2 hover:text-amber-950"
            data-testid="inapp-banner-toggle"
          >
            {expanded ? t("inapp_banner.hide_steps") : t("inapp_banner.show_steps")}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && (
            <ol className="mt-2 list-decimal list-inside space-y-1 text-amber-900">
              {steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("inapp_banner.dismiss")}
          className="shrink-0 -mr-1 p-1 rounded hover:bg-amber-200/60 text-amber-900"
          data-testid="inapp-banner-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
